import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Text, Stack, Group } from "@mantine/core";
import { memo, useCallback } from "react";
import { useAppAction } from "../../hooks/redux.js";
import { useCaptureEvent } from "../../components/StyleEditorContext.js";
import { Event } from "../../constants/events.js";
import { changeLayerPaintConfig } from "../../reducers/layersReducer.js";
import { FormattedMessage } from "react-intl";
import { get, isArray, isEqual, isNumber, isString, set } from "es-toolkit/compat";
import { round } from "es-toolkit";
import { ExpressionInput } from "../../components/ExpressionInput.js";
import { ColorInput } from "../../components/ColorInput.js";
import { SliderInput } from "../../components/SliderInput.js";
const opacityPropertyMapping = {
    "fill": "fill-opacity",
    "line": "line-opacity",
    "raster": "raster-opacity",
    "heatmap": "heatmap-opacity",
    "fill-extrusion": "fill-extrusion-opacity",
    "background": "fill-opacity",
    "circle": "circle-opacity",
    "symbol": "icon-opacity",
    "hillshade": "hillshade-exaggeration",
    "color-relief": "color-relief-opacity",
};
const defaultOpacity = 1;
function opacityLabel(value) {
    return `${round(value * 100)}%`;
}
function getOpacity(type, paint) {
    const propertyName = opacityPropertyMapping[type];
    return get(paint, propertyName, defaultOpacity);
}
function setOpacity(type, paint = {}, opacity) {
    const propertyName = opacityPropertyMapping[type];
    set(paint, propertyName, opacity);
}
const colorPropertyMapping = {
    "fill": "fill-color",
    "line": "line-color",
    "raster": "raster-color",
    "heatmap": "heatmap-color",
    "fill-extrusion": "fill-extrusion-color",
    "background": "fill-color",
    "circle": "circle-color",
    "symbol": "icon-color",
    "hillshade": "hillshade-exaggeration",
    "color-relief": "color-relief-color",
};
const defaultColor = "#000000";
function getColor(type, paint) {
    const propertyName = colorPropertyMapping[type];
    return get(paint, propertyName, defaultColor);
}
function setColor(type, paint = {}, color) {
    const propertyName = colorPropertyMapping[type];
    set(paint, propertyName, color);
}
const updatePaintProperty = (type, layerId, propertyLabel, setter, getter) => {
    const updatePaint = useAppAction(changeLayerPaintConfig);
    const captureEvent = useCaptureEvent();
    return useCallback((layerPaint, value) => {
        const current = getter(type, layerPaint);
        if (!isEqual(current, value)) {
            const updatedPaint = structuredClone(layerPaint);
            setter(type, updatedPaint, value);
            updatePaint({
                layerId: layerId,
                paint: updatedPaint,
                propertyLabel,
                propertyValue: value,
            });
            captureEvent(Event.ChangeLayerPaintConfig, {
                layerId,
                property: propertyLabel,
            });
        }
    }, [type, layerId, propertyLabel, updatePaint, getter, setter, captureEvent]);
};
export const LayerPaintSettings = memo(({ layerId, type, paint = {} }) => {
    const opacity = getOpacity(type, paint);
    const color = getColor(type, paint);
    const updateOpacity = updatePaintProperty(type, layerId, "opacity", setOpacity, getOpacity);
    const updateColor = updatePaintProperty(type, layerId, "color", setColor, getColor);
    return (_jsxs(Stack, { gap: "xs", style: { overflow: "hidden" }, children: [_jsxs(Text, { fz: "sm", fw: 600, children: [type === "fill" && _jsx(FormattedMessage, { id: "label.fill", defaultMessage: "Fill" }), type === "line" && _jsx(FormattedMessage, { id: "label.line", defaultMessage: "Line" }), type === "raster" && _jsx(FormattedMessage, { id: "label.raster", defaultMessage: "Raster" }), type === "heatmap" && _jsx(FormattedMessage, { id: "label.heatmap", defaultMessage: "Heatmap" }), type === "fill-extrusion" && (_jsx(FormattedMessage, { id: "label.fill-extrusion", defaultMessage: "Fill extrusion" })), type === "background" && (_jsx(FormattedMessage, { id: "label.background", defaultMessage: "Background" })), type === "circle" && _jsx(FormattedMessage, { id: "label.circle", defaultMessage: "Circle" }), type === "symbol" && _jsx(FormattedMessage, { id: "label.symbol", defaultMessage: "Symbol" }), type === "hillshade" && (_jsx(FormattedMessage, { id: "label.hillshade", defaultMessage: "Hillshade" })), type === "color-relief" && (_jsx(FormattedMessage, { id: "label.color-relief", defaultMessage: "Color relief" }))] }), isNumber(opacity) && (_jsxs(Stack, { gap: 8, children: [_jsxs(Group, { justify: "space-between", children: [_jsx(Text, { fz: "sm", children: _jsx(FormattedMessage, { id: "label.opacity", defaultMessage: "Opacity" }) }), _jsxs(Text, { fz: "sm", children: [Math.floor(opacity * 100), "%"] })] }), _jsx(SliderInput, { min: 0, max: 1, step: 0.01, value: opacity, sliderLabel: opacityLabel, onChange: (value) => {
                            updateOpacity(paint, value);
                        } })] })), isArray(opacity) && (_jsx(ExpressionInput, { label: _jsx(FormattedMessage, { id: "label.opacity", defaultMessage: "Opacity" }), value: opacity, onChange: (value) => {
                    updateOpacity(paint, value);
                } })), isString(color) && (_jsxs(Stack, { gap: 8, children: [_jsx(Group, { justify: "space-between", children: _jsx(Text, { fz: "sm", children: _jsx(FormattedMessage, { id: "label.color", defaultMessage: "Color" }) }) }), _jsx(ColorInput, { value: color, onChange: (value) => {
                            updateColor(paint, value);
                        } })] })), isArray(color) && (_jsx(ExpressionInput, { label: _jsx(FormattedMessage, { id: "label.color", defaultMessage: "Color" }), value: color, onChange: (value) => {
                    updateColor(paint, value);
                } }))] }));
});
